import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { AuthNotice, AuthShell } from '@/components/layout/AuthShell';
import { getApiErrorMessage } from '@/utils/apiError';

interface LoginLocationState {
  /** 회원가입을 마치고 돌아왔을 때 보여줄 안내 */
  notice?: string;
}

interface FormError {
  message: string;
  /** 자격 증명이 아니라 계정 상태(승인 대기·정지) 때문에 막힌 경우 */
  blocked: boolean;
}

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | undefined)?.response?.status;

export const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<FormError | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const notice = (location.state as LoginLocationState | null)?.notice;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFormError(null);

    try {
      const user = await login({ username, password });
      toast.success('로그인 성공');
      /*
       * 화면 분기는 응답의 role 이 정한다 (계약 §6).
       *
       * 기관 포털에는 `landing` 표시를 달아 보낸다. 포털 설정에서 시작 화면을
       * 골라 둔 담당자를 그 화면으로 넘기라는 신호이며, 이 표시가 있을 때만
       * 넘긴다 — 사이드바로 `/portal` 에 들어올 때까지 넘겨 버리면 기관 현황을
       * 볼 방법이 사라진다 (`PortalLanding` 주석 참고).
       */
      if (user.role === 'institution') {
        navigate('/portal', { replace: true, state: { landing: true } });
      } else {
        navigate('/kiosks', { replace: true });
      }
    } catch (error) {
      /**
       * 승인 대기·정지는 401 이 아니라 403 으로 온다(계약 §2). api.ts 인터셉터가
       * 401 만 로그인 페이지로 강제 이동시키므로, 403 사유는 여기서 그대로 보인다.
       */
      setFormError({
        message: getApiErrorMessage(error, '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.'),
        blocked: statusOf(error) === 403,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title='로그인'
      description='관리자와 기관 담당자가 같은 화면으로 로그인합니다.'
      footer={
        <span className='inline-flex items-center gap-1.5 flex-wrap justify-center'>
          기관 담당자이신가요?
          <Link
            to='/signup'
            className='inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700 hover:underline'
          >
            <UserPlus className='w-4 h-4' />
            기관 담당자 회원가입
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className='space-y-4'>
        {notice && (
          <AuthNotice tone='success' title='가입 신청이 접수되었습니다'>
            {notice}
          </AuthNotice>
        )}

        {formError && (
          <AuthNotice
            tone={formError.blocked ? 'warning' : 'error'}
            title={formError.blocked ? '아직 이용할 수 없는 계정입니다' : '로그인 실패'}
          >
            {formError.message}
          </AuthNotice>
        )}

        <Input
          label='아이디'
          type='text'
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder='아이디를 입력하세요'
          autoComplete='username'
          required
        />

        <Input
          label='비밀번호'
          type='password'
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder='••••••••'
          autoComplete='current-password'
          required
        />

        <Button type='submit' className='w-full' loading={loading}>
          로그인
        </Button>
      </form>
    </AuthShell>
  );
};
